import { useEffect, useState } from "react";
import axios from "axios";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { useBriefing } from "@/context/BriefingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENT_OPTIONS = [
  "Investment interest",
  "Strategic partnership",
  "Policy / industrial dialogue",
  "Pharmaceutical operator",
  "Press / editorial",
  "Other",
];

const INITIAL = {
  name: "",
  email: "",
  organization: "",
  title: "",
  intent: "",
  note: "",
};

export default function BriefingForm() {
  const { open, setOpen, closeBriefing } = useBriefing();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      // reset shortly after close
      const t = setTimeout(() => {
        setForm(INITIAL);
        setConfirmed(false);
        setErrors({});
        setSubmitting(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required.";
    if (!form.email.trim()) e.email = "Required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email address.";
    if (!form.organization.trim()) e.organization = "Required.";
    if (!form.title.trim()) e.title = "Required.";
    if (!form.intent) e.intent = "Select one.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) =>
    setForm((s) => ({ ...s, [k]: ev.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/briefings`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        title: form.title.trim(),
        intent: form.intent,
        note: form.note.trim() || null,
      });
      setConfirmed(true);
      toast("Briefing request received.");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg =
        typeof raw === "string"
          ? raw
          : "Submission could not be recorded. Please review the fields and try again.";
      toast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="briefing-overlay"
          className="fixed inset-0 z-[90] bg-[color:var(--g1-ink)]/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          data-testid="briefing-dialog"
          aria-describedby={undefined}
          className="fixed z-[100] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-24px)] max-w-[880px] max-h-[92vh] overflow-y-auto bg-[color:var(--g1-bone)] border border-[color:var(--g1-ink)] rounded-none p-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          {/* Close control */}
          <DialogPrimitive.Close
            data-testid="briefing-close"
            className="absolute right-0 top-0 z-10 w-12 h-12 flex items-center justify-center border-l border-b border-[color:var(--g1-rule)] hover:bg-[color:var(--g1-ink)] hover:text-[color:var(--g1-bone)] transition-colors"
            aria-label="Close"
          >
            <span aria-hidden>×</span>
          </DialogPrimitive.Close>

          {/* Header */}
          <div className="px-6 md:px-12 pt-10 md:pt-14 pb-6 border-b border-[color:var(--g1-rule)]">
            <div className="flex items-center justify-between">
              <div className="g1-label text-[color:var(--g1-ink-3)]">
                / REQUEST A BRIEFING
              </div>
            </div>
            <DialogPrimitive.Title asChild>
              <h2 className="font-serif text-[32px] md:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.02em] mt-6 max-w-[18ch]">
                {confirmed
                  ? "Briefing request received."
                  : "Speak with Giga One."}
              </h2>
            </DialogPrimitive.Title>
            {!confirmed && (
              <p className="g1-body mt-4 text-[color:var(--g1-ink-2)]">
                Briefings are granted on a reviewed basis. Please provide a
                few details. All communications are held in confidence.
              </p>
            )}
          </div>

          {/* Body */}
          {confirmed ? (
            <div
              data-testid="briefing-confirmation"
              className="px-6 md:px-12 py-12 md:py-16"
            >
              <p className="g1-lede text-[color:var(--g1-ink-2)]">
                Thank you, {form.name.split(" ")[0] || "—"}. Your request has
                been logged under our briefings registry. A member of the team
                will respond directly to{" "}
                <span className="text-[color:var(--g1-ink)]">
                  {form.email}
                </span>
                .
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  data-testid="briefing-confirmation-close"
                  onClick={closeBriefing}
                  className="g1-btn g1-btn-primary"
                >
                  Close
                  <span className="g1-btn-arrow" aria-hidden>
                    →
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <form
              data-testid="briefing-form"
              onSubmit={onSubmit}
              className="px-6 md:px-12 py-8 md:py-12"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <Field
                  label="Full name"
                  name="name"
                  value={form.name}
                  onChange={onChange("name")}
                  error={errors.name}
                  autoComplete="name"
                  testId="briefing-input-name"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  error={errors.email}
                  autoComplete="email"
                  testId="briefing-input-email"
                />
                <Field
                  label="Organization"
                  name="organization"
                  value={form.organization}
                  onChange={onChange("organization")}
                  error={errors.organization}
                  autoComplete="organization"
                  testId="briefing-input-organization"
                />
                <Field
                  label="Title / role"
                  name="title"
                  value={form.title}
                  onChange={onChange("title")}
                  error={errors.title}
                  autoComplete="organization-title"
                  testId="briefing-input-title"
                />
                <div className="md:col-span-2">
                  <label className="g1-label block mb-2">
                    Interest / intent
                  </label>
                  <select
                    data-testid="briefing-input-intent"
                    className="g1-select"
                    value={form.intent}
                    onChange={onChange("intent")}
                  >
                    <option value="" disabled>
                      Select one —
                    </option>
                    {INTENT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {errors.intent && (
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-red-700 mt-2">
                      {errors.intent}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="g1-label block mb-2">
                    Optional note
                  </label>
                  <textarea
                    data-testid="briefing-input-note"
                    className="g1-textarea"
                    value={form.note}
                    onChange={onChange("note")}
                    placeholder="Context, questions, timing —"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-10 md:mt-12 pt-8 border-t border-[color:var(--g1-rule)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--g1-ink-3)] max-w-[46ch]">
                  By submitting, you agree to be contacted regarding a
                  strategic briefing.
                </p>
                <button
                  type="submit"
                  data-testid="briefing-submit"
                  disabled={submitting}
                  className="g1-btn g1-btn-primary"
                >
                  {submitting ? "Sending…" : "Submit Request"}
                  <span className="g1-btn-arrow" aria-hidden>
                    →
                  </span>
                </button>
              </div>
            </form>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Field({ label, name, type = "text", value, onChange, error, testId, autoComplete }) {
  return (
    <div>
      <label htmlFor={name} className="g1-label block mb-2">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className="g1-input"
        data-testid={testId}
        autoComplete={autoComplete}
      />
      {error && (
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-red-700 mt-2">
          {error}
        </p>
      )}
    </div>
  );
}
